// Dashboard API Simulation
// This file contains simulated API functions and dummy data for the dashboard

import api from '../../../api';
import { getPeriodDateRange } from '../../../components/common/AdvancedFilter/periodUtils';

// Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
const toFilterParam = (v) => {
  if (Array.isArray(v)) {
    const clean = v.filter((x) => x != null && x !== '' && x !== 'all');
    return clean.length === 0 ? 'all' : clean.join(',');
  }
  return v == null || v === '' ? 'all' : String(v);
};

// Helper function to generate date ranges
const getDateRange = (period, customStartDate = null, customEndDate = null) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Handle custom date range
  if (period === 'custom' && customStartDate && customEndDate) {
    return {
      start: new Date(customStartDate),
      end: new Date(customEndDate)
    };
  }
  
  switch (period) {
    case 'last_3_days':
      return {
        start: new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000),
        end: today
      };
    case 'last_7_days':
      return {
        start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: today
      };
    case 'this_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: today
      };
    case 'last_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0)
      };
    case 'this_year':
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: today
      };
    case 'last_year':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear() - 1, 11, 31)
      };
    default:
      return {
        start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: today
      };
  }
};

// Helper function to calculate period multiplier based on date range
const calculatePeriodMultiplier = (period, customStartDate = null, customEndDate = null) => {
  if (period === 'custom' && customStartDate && customEndDate) {
    const startDate = new Date(customStartDate);
    const endDate = new Date(customEndDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Calculate multiplier based on number of days
    // Use last_month (30 days) as base reference
    return Math.max(0.1, Math.min(12.0, daysDiff / 30));
  }
  
  const periodMultiplier = {
    'last_3_days': 0.1,
    'last_7_days': 0.2,
    'this_month': 0.8,
    'last_month': 1.0,
    'this_year': 8.0,
    'last_year': 12.0
  };
  
  return periodMultiplier[period] || 1.0;
};

// Generate dummy statistics data based on filters
const generateStatisticsData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, people = 'all', businessUnits = 'all', sector = 'all', source = 'all' } = filters;
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  
  return {
    totalProspect: Math.floor(1250 * multiplier),
    newProspect: Math.floor(180 * multiplier),
    convertedToProposal: Math.floor(95 * multiplier),
    totalLeads: Math.floor(890 * multiplier),
    totalProjects: Math.floor(156 * multiplier)
  };
};

// Generate dummy chart data for Companies Contacted per Team Member
const generateTeamMemberData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
  const teamMembers = [
    ['John', 'Doe'], ['Joe', 'Smith'], ['Jake', 'Williams'], 'Amber',
    ['Peter', 'Brown'], ['Mary', 'Evans'], ['David', 'Wilson'], ['Lily', 'Roberts']
  ];
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  
  const baseData = [21, 22, 10, 28, 16, 21, 13, 30];
  
  return {
    data: baseData.map(value => Math.floor(value * multiplier)),
    categories: teamMembers
  };
};

// Generate dummy sales funnel data - DEPRECATED: Now using real API
const generateSalesFunnelData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  const baseData = [1380, 1100, 990, 880];
  
  return {
    data: baseData.map(value => Math.floor(value * multiplier)),
    categories: ['Leads', 'Proposal', 'Negotiation', 'Contract/Won', 'Project']
  };
};

// Generate dummy proposal breakdown data
const generateProposalBreakdownData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  const baseData = [25, 35, 20];
  
  return {
    series: baseData.map(value => Math.floor(value * multiplier)),
    labels: ["Draft", "Terkirim", "Diterima"],
    colors: ["#FF4560", "#00E396", "#008FFB"]
  };
};

// // Generate dummy leads by source data
// const generateLeadsBySourceData = (filters = {}) => {
//   const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
//   let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
//   // Adjust multiplier based on businessUnits selection
//   if (businessUnits !== 'all') {
//     const businessUnitMultipliers = {
//       'Sales Team': 1.2,
//       'Marketing Team': 0.8,
//       'Support Team': 0.6
//     };
//     multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
//   }
//   const baseData = [25, 35, 20, 15, 5];
  
//   return {
//     series: baseData.map(value => Math.floor(value * multiplier)),
//     labels: ["External Marketing", "Website", "Event", "Email", "Consultant"]
//   };
// };

// Generate dummy leads by industry sector data
const generateLeadsByIndustryData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Retail',
    'Energy', 'Education', 'Transportation', 'Hospitality'
  ];
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  const baseData = [21, 22, 10, 28, 16, 21, 13, 30];
  
  return {
    data: baseData.map(value => Math.floor(value * multiplier)),
    categories: industries
  };
};

// Generate dummy task management data
const generateTaskManagementData = (filters = {}) => {
  const { period = 'last_month', customStartDate = null, customEndDate = null, businessUnits = 'all' } = filters;
  
  let multiplier = calculatePeriodMultiplier(period, customStartDate, customEndDate);
  
  // Adjust multiplier based on businessUnits selection
  if (businessUnits !== 'all') {
    const businessUnitMultipliers = {
      'Sales Team': 1.2,
      'Marketing Team': 0.8,
      'Support Team': 0.6
    };
    multiplier *= (businessUnitMultipliers[businessUnits] || 1.0);
  }
  
  return [
    {
      title: "Follow Up Calls",
      status: `${Math.floor(24 * multiplier)} In Progress`,
      bgColor: "#F2F7FF",
      dotColor: "#3899FE",
      badgeColor: "#D8EBFF",
      textColor: "#1E81E8"
    },
    {
      title: "Proposal Documentation",
      status: `${Math.floor(8 * multiplier)} In Progress`,
      bgColor: "#FFF8E6",
      dotColor: "#FFA705",
      badgeColor: "#FFEECF",
      textColor: "#BF7C00"
    },
    {
      title: "Contract Review",
      status: `${Math.floor(3 * multiplier)} In Progress`,
      bgColor: "#EFFFE6",
      dotColor: "#5EBA30",
      badgeColor: "#D7FAC6",
      textColor: "#166534"
    },
    {
      title: "Client Meetings",
      status: `${Math.floor(12 * multiplier)} In Progress`,
      bgColor: "#F4F2FF",
      dotColor: "#8B5CF6",
      badgeColor: "#EFE0FF",
      textColor: "#6B21A8"
    }
  ];
};

// Simulated API functions
export const dashboardApi = {
  // Get dashboard statistics
  getStatistics: async (filters = {}) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      success: true,
      data: generateStatisticsData(filters)
    };
  },

  // Get team member data for chart from backend API
  getTeamMemberData: async (filters = {}) => {
    try {
      const { period = 'last_month', customStartDate, customEndDate } = filters;
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);
      let queryParams = `period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`;
      
      // ✅ PERBAIKAN: Add custom date range parameters if period is 'custom'
      if (period === 'custom' && customStartDate && customEndDate) {
        queryParams += `&customStartDate=${customStartDate}&customEndDate=${customEndDate}`;
      }
      
      const response = await api.get(`/dashboard/team-member-contacts?${queryParams}`);
      
      // Backend returns: { data: [21, 22, 10, 28, 16, 21, 13, 30], categories: ["John Doe", "Joe Smith", ...] }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching team member contacts data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: { data: [], categories: [] }
      };
    }
  },

  // Get sales funnel data from backend API
  getSalesFunnelData: async (filters = {}) => {
    try {
      const period = filters.period || 'last_month';
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);
      const response = await api.get(`/dashboard/sales-funnel?period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`);
      
      // Backend returns: { data: [count, count, count, count, count], categories: ["Leads", "Proposal", "Negotiation", "Contract/Won", "Project"] }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching sales funnel data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: { data: [], categories: [] }
      };
    }
  },

  // Get proposal breakdown data from backend API
  getProposalBreakdownData: async (filters = {}) => {
    try {
      const period = filters.period || 'last_month';
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);
      const response = await api.get(`/dashboard/proposal-breakdown?period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`);
      
      console.log('Proposal breakdown API response:', response.data);
      
      // Backend returns: { series: [37, 5, 11], labels: ["Draft", "Send to Client", "Accepted"], colors: ["#FF4560", "#00E396", "#008FFB"] }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching proposal breakdown data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: { series: [], labels: [], colors: [] }
      };
    }
  },

  // Get leads by source data from backend API
  getLeadsBySourceData: async (filters = {}) => {
    try {
      const period = filters.period || 'last_month';
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);
      const response = await api.get(`/dashboard/leads-by-source?period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`);
      
      console.log('Leads by source API response:', response.data);
      
      // Backend returns: { series: [25, 35, 20, 15, 5], labels: ["External Marketing", "Website", "Event", "Email", "Consultant"] }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching leads by source data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: { series: [], labels: [] }
      };
    }
  },

  // Get leads by industry data from backend API
  getLeadsByIndustryData: async (filters = {}) => {
    try {
      const period = filters.period || 'last_month';
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);
      const response = await api.get(`/dashboard/leads-by-industry?period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`);
      
      console.log('Leads by industry API response:', response.data);
      
      // Backend returns: { data: [21, 22, 10, 28, 16, 21, 13, 30], categories: ["Technology", "Healthcare", "Finance", ...] }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching leads by industry data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: { data: [], categories: [] }
      };
    }
  },

  // Get task management data from backend API
  getTaskManagementData: async (filters = {}) => {
    try {
      const period = filters.period || 'last_month';
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const response = await api.get(`/dashboard/task-management?period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}`);
      
      console.log('Task management API response:', response.data);
      
      // Backend returns: { tasks: [{ title: "Follow Up Calls", status: "24 In Progress", bgColor: "#F2F7FF", ... }, ...] }
      return {
        success: true,
        data: response.data.tasks || []
      };
    } catch (error) {
      console.error('Error fetching task management data:', error);
      // Return empty data instead of fallback
      return {
        success: false,
        data: []
      };
    }
  },

  // Get all dashboard data at once
  getAllDashboardData: async (filters = {}) => {
    try {
      // ✅ PERBAIKAN: Initialize dengan nilai 0 dan array kosong, JANGAN gunakan dummy data
      const defaultData = {
        teamMemberData: { data: [], categories: [], userIds: [] },
        // Keep full shape so UI can show value labels when backend provides them
        salesFunnelData: {
          data: [],
          categories: [],
          proposalValue: 0,
          formattedProposalValue: '',
          negotiationValue: 0,
          contractWonValue: 0,
          projectsValue: 0
        },
        proposalBreakdownData: { series: [], labels: [], colors: [] },
        leadsBySourceData: { series: [], labels: [] },
        leadsByIndustryData: { data: [], categories: [] },
        taskManagementData: []
      };

      // Try to fetch each API individually and handle errors gracefully
      const { period = 'last_month', customStartDate, customEndDate } = filters;
      const source = toFilterParam(filters.source);
      const sector = toFilterParam(filters.sector);
      const standard = toFilterParam(filters.standard);
      const service = toFilterParam(filters.service);
      const businessUnit = toFilterParam(filters.businessUnits);
      const userId = toFilterParam(filters.people);
      const branchId = (filters.branchId == null || filters.branchId === '' || filters.branchId === 'all') ? 'all' : String(filters.branchId);

      // Build query parameters string
      let queryParams = `period=${period}&source=${source}&sector=${sector}&standard=${standard}&service=${service}&businessUnit=${businessUnit}&userId=${userId}&branchId=${branchId}`;
      
      // ✅ PERBAIKAN: Add custom date range parameters if period is 'custom'
      if (period === 'custom' && customStartDate && customEndDate) {
        queryParams += `&customStartDate=${customStartDate}&customEndDate=${customEndDate}`;
      }
      
      try {
        const salesFunnelResponse = await api.get(`/dashboard/sales-funnel?${queryParams}`);
        defaultData.salesFunnelData = {
          ...defaultData.salesFunnelData,
          ...(salesFunnelResponse.data || {})
        };
      } catch (error) {
        console.error('Error fetching sales funnel data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.salesFunnelData = {
          ...defaultData.salesFunnelData,
          data: [],
          categories: []
        };
      }

      try {
        const teamMemberResponse = await api.get(`/dashboard/team-member-contacts?${queryParams}`);
        // Backend returns: { data: [...], categories: [...], userIds: [...] }
        // Ensure userIds array is included (backend should return it)
        const responseData = teamMemberResponse.data || {};
        defaultData.teamMemberData = {
          data: responseData.data || [],
          categories: responseData.categories || [],
          userIds: responseData.userIds || []
        };
      } catch (error) {
        console.error('Error fetching team member contacts data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.teamMemberData = { data: [], categories: [], userIds: [] };
      }

      try {
        const proposalBreakdownResponse = await api.get(`/dashboard/proposal-breakdown?${queryParams}`);
        defaultData.proposalBreakdownData = proposalBreakdownResponse.data;
      } catch (error) {
        console.error('Error fetching proposal breakdown data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.proposalBreakdownData = { series: [], labels: [], colors: [] };
      }

      try {
        const leadsBySourceResponse = await api.get(`/dashboard/leads-by-source?${queryParams}`);
        defaultData.leadsBySourceData = leadsBySourceResponse.data;
      } catch (error) {
        console.error('Error fetching leads by source data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.leadsBySourceData = { series: [], labels: [] };
      }

      try {
        const leadsByIndustryResponse = await api.get(`/dashboard/leads-by-industry?${queryParams}`);
        defaultData.leadsByIndustryData = leadsByIndustryResponse.data;
      } catch (error) {
        console.error('Error fetching leads by industry data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.leadsByIndustryData = { data: [], categories: [] };
      }

      try {
        // Build due date parameters for task filtering
        const taskParams = new URLSearchParams();

        // Add due date filtering based on dashboard period
        if (period !== 'all') {
          if (period === 'custom' && customStartDate && customEndDate) {
            taskParams.append('dueDateFrom', customStartDate);
            taskParams.append('dueDateTo', customEndDate);
          } else {
            const range = getPeriodDateRange(period);
            if (range.startDate && range.endDate) {
              taskParams.append('dueDateFrom', range.startDate);
              taskParams.append('dueDateTo', range.endDate);
            }
          }
        }

        // Fetch tasks with due date filtering
        const taskQueryString = taskParams.toString();
        const taskUrl = taskQueryString ? `/tasks?${taskQueryString}` : '/tasks';
        const taskResponse = await api.get(taskUrl);
        const allTasks = Array.isArray(taskResponse.data) ? taskResponse.data : [];

        // Helper: parse comma-separated param into array of IDs (empty if 'all')
        const toIdList = (param) => (!param || param === 'all' ? [] : String(param).split(',').map(s => s.trim()).filter(Boolean));

        // Filter tasks by assigned user(s) if specified
        let filteredTasks = allTasks;
        const userIds = toIdList(userId);
        if (userIds.length > 0) {
          filteredTasks = allTasks.filter(task => {
            const tid = task.assignedUserId;
            return tid != null && userIds.some(uid =>
              tid === parseInt(uid, 10) || String(tid) === String(uid)
            );
          });
        }

        // ✅ Filter tasks by sector(s) if specified
        const sectorIds = toIdList(sector);
        if (sectorIds.length > 0) {
          filteredTasks = filteredTasks.filter(task => {
            const taskSectorId = task.sectorId;
            return taskSectorId != null && sectorIds.some(sid =>
              taskSectorId === parseInt(sid, 10) || String(taskSectorId) === String(sid)
            );
          });
        }

        // ✅ Filter tasks by source(s) if specified
        const sourceIds = toIdList(source);
        if (sourceIds.length > 0) {
          filteredTasks = filteredTasks.filter(task => {
            const taskSourceId = task.sourceId;
            return taskSourceId != null && sourceIds.some(sid =>
              taskSourceId === parseInt(sid, 10) || String(taskSourceId) === String(sid)
            );
          });
        }

        // ✅ Filter tasks by standard(s) if specified
        const standardIds = toIdList(standard);
        if (standardIds.length > 0) {
          filteredTasks = filteredTasks.filter(task => {
            const taskStandardIds = task.standardIds;
            if (!taskStandardIds || taskStandardIds.length === 0) return false;
            return taskStandardIds.some(tid =>
              standardIds.some(sid =>
                String(tid) === String(sid) || tid === parseInt(sid, 10)
              )
            );
          });
        }

        // Group tasks by type and count only active tasks (to do and in progress)
        const taskGroups = {};
        filteredTasks.forEach(task => {
          const type = task.type || 'other';
          const status = task.status?.toLowerCase() || 'unknown';

          // Only count tasks that are "to do" or "in progress" (exclude "done")
          if (status === 'to do' || status === 'in progress') {
            if (!taskGroups[type]) {
              taskGroups[type] = {
                to_do: 0,
                in_progress: 0,
                total_active: 0
              };
            }

            if (status === 'to do') taskGroups[type].to_do++;
            else if (status === 'in progress') taskGroups[type].in_progress++;

            // Update total active tasks
            taskGroups[type].total_active = taskGroups[type].to_do + taskGroups[type].in_progress;
          }
        });

        // Convert to the expected format for task management display
        let taskManagementData = Object.entries(taskGroups).map(([type, counts]) => {
          // Task types are now stored in title case format (e.g., "Follow Up", "Email")
          const typeDisplayNames = {
            'Follow Up': 'Follow Up Calls',
            'Follow Up Email': 'Follow Up Email',
            'Follow Up Meeting': 'Follow Up Meeting',
            'Follow Up Call': 'Follow Up Call',
            'Proposal': 'Proposal Documentation',
            'Sent Proposal': 'Sent Proposal',
            'Contract': 'Contract Review',
            'Meeting': 'Client Meetings',
            'Client Meetings': 'Client Meetings',
            'Reminder': 'Reminder',
            'Email': 'Email',
            'email': 'Email',
            'Call': 'Call',
            'Call No Answer': 'Call No Answer',
            'Telegram': 'Telegram',
            'Data Update': 'Data Update',
            'Invoice': 'Invoice',
            'Other': 'Other Tasks',
            'Other Tasks': 'Other Tasks'
          };

          // First try exact match, then try lowercase match for backward compatibility
          const displayName = typeDisplayNames[type] || typeDisplayNames[type.toLowerCase()] || type;

          // Only include task types that have active tasks
          if (counts.total_active === 0) return null;

          // Define colors based on task type (now using title case format)
          const typeColors = {
            // Follow-up related tasks (Blue theme)
            'Follow Up': { bgColor: "#F2F7FF", dotColor: "#3899FE", badgeColor: "#D8EBFF", textColor: "#1E81E8" },
            'Follow Up Email': { bgColor: "#F2F7FF", dotColor: "#3899FE", badgeColor: "#D8EBFF", textColor: "#1E81E8" },
            'Follow Up Meeting': { bgColor: "#F2F7FF", dotColor: "#3899FE", badgeColor: "#D8EBFF", textColor: "#1E81E8" },
            'Follow Up Call': { bgColor: "#F2F7FF", dotColor: "#3899FE", badgeColor: "#D8EBFF", textColor: "#1E81E8" },

            // Proposal & Contract (Orange/Green theme)
            'Proposal': { bgColor: "#FFF8E6", dotColor: "#FFA705", badgeColor: "#FFEECF", textColor: "#BF7C00" },
            'Sent Proposal': { bgColor: "#FFF8E6", dotColor: "#FFA705", badgeColor: "#FFEECF", textColor: "#BF7C00" },
            'Contract': { bgColor: "#EFFFE6", dotColor: "#5EBA30", badgeColor: "#D7FAC6", textColor: "#166534" },

            // Communication tasks (Blue/Purple/Cyan theme)
            'Email': { bgColor: "#E6F7FF", dotColor: "#4FC3F7", badgeColor: "#CCE7FF", textColor: "#01579B" },
            'email': { bgColor: "#E6F7FF", dotColor: "#4FC3F7", badgeColor: "#CCE7FF", textColor: "#01579B" },
            'Call': { bgColor: "#F3E6FF", dotColor: "#BA68C8", badgeColor: "#E8CCFF", textColor: "#6A1B9A" },
            'Call No Answer': { bgColor: "#F3E6FF", dotColor: "#BA68C8", badgeColor: "#E8CCFF", textColor: "#6A1B9A" },
            'Telegram': { bgColor: "#FFF2E6", dotColor: "#FF9800", badgeColor: "#FFE0B2", textColor: "#E65100" },

            // Meeting tasks (Purple theme)
            'Meeting': { bgColor: "#F4F2FF", dotColor: "#8B5CF6", badgeColor: "#EFE0FF", textColor: "#6B21A8" },
            'Client Meetings': { bgColor: "#F4F2FF", dotColor: "#8B5CF6", badgeColor: "#EFE0FF", textColor: "#6B21A8" },

            // Reminder tasks (Red theme)
            'Reminder': { bgColor: "#FFE6E6", dotColor: "#FF6B6B", badgeColor: "#FFD6D6", textColor: "#B91C1C" },

            // Data & Administrative tasks (Teal/Yellow theme)
            'Data Update': { bgColor: "#E6F7F0", dotColor: "#10B981", badgeColor: "#CCF4E0", textColor: "#065F46" },

            // Invoice & Financial tasks (Amber theme)
            'Invoice': { bgColor: "#FFFBEB", dotColor: "#F59E0B", badgeColor: "#FEF3C7", textColor: "#92400E" },

            // Other tasks (Gray theme - default)
            'Other Tasks': { bgColor: "#F8F9FA", dotColor: "#6C757D", badgeColor: "#E9ECEF", textColor: "#495057" },
            'Other': { bgColor: "#F8F9FA", dotColor: "#6C757D", badgeColor: "#E9ECEF", textColor: "#495057" }
          };

          // Try exact match first, then try lowercase match for backward compatibility
          const colors = typeColors[type] || typeColors[type.toLowerCase()] || { bgColor: "#F8F9FA", dotColor: "#6C757D", badgeColor: "#E9ECEF", textColor: "#495057" };

          return {
            title: displayName,
            toDoCount: counts.to_do,
            inProgressCount: counts.in_progress,
            totalActive: counts.total_active,
            bgColor: colors.bgColor,
            dotColor: colors.dotColor,
            badgeColor: colors.badgeColor,
            textColor: colors.textColor
          };
        }).filter(item => item !== null); // Filter out null entries (task types with no active tasks)

        // Sort by total active tasks count (highest first)
        taskManagementData.sort((a, b) => b.totalActive - a.totalActive);

        defaultData.taskManagementData = taskManagementData;
      } catch (error) {
        console.error('Error fetching task management data:', error);
        // Set 0/empty - jangan gunakan fallback
        defaultData.taskManagementData = [];
      }

      return {
        success: true,
        data: defaultData
      };
    } catch (error) {
      console.error('Error in getAllDashboardData:', error);
      // ✅ PERBAIKAN: Return nilai 0/empty, JANGAN gunakan dummy data
      return {
        success: false,
        data: {
          teamMemberData: { data: [], categories: [], userIds: [] },
          salesFunnelData: { data: [], categories: [] },
          proposalBreakdownData: { series: [], labels: [], colors: [] },
          leadsBySourceData: { series: [], labels: [] },
          leadsByIndustryData: { data: [], categories: [] },
          taskManagementData: []
        }
      };
    }
  }
};

export default dashboardApi;
